import React from 'react';
import Head from 'next/head';
import getConfig from 'next/config';

import {utcToIso8601} from '~/src/utilities/helpers/date';

import {Variants} from '~/src/types/image';
import {Author} from '~/src/types/meta';

const {publicRuntimeConfig: config} = getConfig();

interface StructuredArticleProps {
    datePublished: string;
    dateModified: string;
    authors: Author[];
    headline: string;
    description: string;
    images: Variants;
    canonicalUrl: string;
}

const StructuredArticle = ({datePublished, dateModified, authors = [], headline, description, images, canonicalUrl}: StructuredArticleProps) => {
    const author = authors.length
        ? authors.map((author) => ({
              '@type': 'Person',
              name: author.name
          }))
        : {'@type': 'NewsMediaOrganization', name: config.tenantName};

    const image =
        images &&
        ['fix1200x1200px', 'fix1200x900px', 'fix1200x675px']
            .filter((variant) => images[variant])
            .map((variant) => ({
                '@type': 'ImageObject',
                url: images[variant].src,
                width: images[variant].width,
                height: images[variant].height
            }));

    const structuredData = {
        '@context': 'http://schema.org',
        '@type': 'NewsArticle',
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': canonicalUrl
        },
        publisher: {
            '@type': 'Organization',
            name: config.tenantName,
            logo: {
                '@type': 'ImageObject',
                url: `${config.canonicalBaseUrl}${config.mainLogoPath}`,
                width: 144,
                height: 192
            }
        },
        headline,
        image,
        datePublished: utcToIso8601(datePublished),
        dateModified: utcToIso8601(dateModified),
        author,
        description
    };

    return (
        <Head>
            <script key="structuredData" type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(structuredData)}}></script>
        </Head>
    );
};

export default StructuredArticle;
